import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { toJS } from 'mobx';
import { userStore, bookStore, notificationStore } from '../../store';
import { InternetMessage, Loader } from '../../components/shared';
import NotificationDrawer from '../../components/shared/NotificationDrawer';
import { Book } from '../../types';
import { Bell, Search } from 'lucide-react';

// Home screen — v2 design (2026-05-30).
//
// Stack:
//   - Top bar: "Home" title + notification bell.
//   - Search pill: tappable, navigates to /app/search.
//   - Featured row: horizontal carousel of 3 cards, each with cover +
//     title + author. "See all" link top-right.
//   - Popular Books row: horizontal carousel of 4 cards, each with cover,
//     title, author, and rating. "See all" link top-right.
//   - Categories row: four pill chips with brand colors + icons. Tapping a
//     chip routes to /app/search?category=<name>.
//
// Pure presentational over the existing mobx stores (no schema changes).

const BRAND = '#5B47E0';
const TEXT_PRIMARY = '#0F172A';
const TEXT_MUTED = '#64748B';
const SURFACE = '#F1F5F9';

// Category chip palette matches the screenshot Mike provided.
const CATEGORY_CHIPS = [
  { name: 'Fiction',  icon: '📖', bg: '#FEE2E2', fg: '#DC2626' },
  { name: 'Romance',  icon: '💝', bg: '#FCE7F3', fg: '#DB2777' },
  { name: 'Mystery',  icon: '🔍', bg: '#DBEAFE', fg: '#2563EB' },
  { name: 'Self-Help',icon: '🌱', bg: '#FED7AA', fg: '#EA580C' },
];

const HomeScreen: React.FC = observer(() => {
  const navigate = useNavigate();
  const [notifOpen, setNotifOpen] = useState(false);

  const featured = useMemo(() => {
    return bookStore.featuredBooks.slice(0, 6);
  }, [bookStore.featuredBooks]);

  const popular = useMemo(() => {
    // Take the latest 8 books by createdAt as a stand-in for "popular".
    // When the backend exposes a popularity metric we can swap this.
    return [...userStore.adverBooks]
      .sort((a, b) => {
        const av = (a as any).createdAt || '';
        const bv = (b as any).createdAt || '';
        return bv > av ? 1 : -1;
      })
      .slice(0, 8);
  }, [userStore.adverBooks]);

  return (
    <div className="h-full flex flex-col" style={{ background: '#FFFFFF' }}>
      <Loader visible={userStore.adverBookLoader && userStore.adverBooks.length === 0} />
      <InternetMessage />

      {/* === Top bar === */}
      <div className="safe-area-top" style={{ background: '#FFFFFF' }}>
        <div className="px-5 pt-4 pb-3 flex items-center justify-between">
          <h1
            className="text-[28px] font-bold tracking-tight"
            style={{ color: TEXT_PRIMARY, lineHeight: 1.1 }}
          >
            Home
          </h1>
          <button
            aria-label="Notifications"
            onClick={() => setNotifOpen(true)}
            className="relative w-10 h-10 flex items-center justify-center rounded-full transition-colors"
            style={{ background: SURFACE }}
          >
            <Bell className="w-[20px] h-[20px]" style={{ color: TEXT_PRIMARY }} strokeWidth={1.8} />
            {notificationStore.unreadCount > 0 && (
              <span
                className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold text-white flex items-center justify-center"
                style={{
                  background: '#EF4444',
                  border: '2px solid #FFFFFF',
                  lineHeight: 1,
                }}
                aria-label={`${notificationStore.unreadCount} unread notifications`}
              >
                {notificationStore.unreadCount > 99 ? '99+' : notificationStore.unreadCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* === Scrollable content === */}
      <div className="flex-1 overflow-y-auto pb-6">
        {/* Search pill — opens Discover with the input auto-focused so the
            keyboard slides up immediately on a phone. */}
        <button
          onClick={() => navigate('/app/search?focus=1')}
          className="mx-5 mt-1 mb-6 w-[calc(100%-2.5rem)] flex items-center gap-3 px-4 h-12 rounded-2xl transition-colors"
          style={{ background: SURFACE }}
        >
          <Search className="w-[18px] h-[18px]" style={{ color: TEXT_MUTED }} strokeWidth={2} />
          <span className="text-[15px]" style={{ color: TEXT_MUTED }}>
            Search books, authors...
          </span>
        </button>

        {/* === Featured === */}
        {featured.length > 0 && (
          <Section
            title="Featured"
            actionLabel="See all"
            onAction={() => navigate('/app/popular?window=all')}
          >
            <HorizontalScroll>
              {featured.map((book) => (
                <FeaturedCard key={book._id} book={book} onClick={() => openBook(navigate, book)} />
              ))}
            </HorizontalScroll>
          </Section>
        )}

        {/* === Popular === */}
        {popular.length > 0 && (
          <Section
            title="Popular Books"
            actionLabel="See all"
            onAction={() => navigate('/app/popular?window=week')}
          >
            <HorizontalScroll>
              {popular.map((book) => (
                <PopularCard key={book._id} book={book} onClick={() => openBook(navigate, book)} />
              ))}
            </HorizontalScroll>
          </Section>
        )}

        {/* === Categories === */}
        <Section
          title="Categories"
          actionLabel="See all"
          onAction={() => navigate('/app/browse')}
        >
          <div className="px-5 grid grid-cols-4 gap-3">
            {CATEGORY_CHIPS.map((c) => (
              <button
                key={c.name}
                onClick={() => navigate(`/app/search?category=${encodeURIComponent(c.name)}`)}
                className="flex flex-col items-center gap-2 transition-transform active:scale-95"
              >
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl"
                  style={{ background: c.bg }}
                >
                  <span aria-hidden="true">{c.icon}</span>
                </div>
                <span
                  className="text-[13px] font-medium"
                  style={{ color: c.fg }}
                >
                  {c.name}
                </span>
              </button>
            ))}
          </div>
        </Section>
      </div>

      {/* Notifications drawer */}
      <NotificationDrawer open={notifOpen} onClose={() => setNotifOpen(false)} />
    </div>
  );
});

/* ====================================================================
 *  Subcomponents
 * ==================================================================== */

function Section({
  title,
  actionLabel,
  onAction,
  children,
}: {
  title: string;
  actionLabel?: string;
  onAction?: () => void;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-7">
      <div className="px-5 mb-3 flex items-center justify-between">
        <h2 className="text-[18px] font-semibold tracking-tight" style={{ color: TEXT_PRIMARY }}>
          {title}
        </h2>
        {actionLabel && onAction && (
          <button
            onClick={onAction}
            className="text-[14px] font-medium"
            style={{ color: BRAND }}
          >
            {actionLabel}
          </button>
        )}
      </div>
      {children}
    </section>
  );
}

function HorizontalScroll({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="flex gap-3 overflow-x-auto px-5 pb-2"
      style={{ scrollbarWidth: 'none' }}
    >
      {children}
      {/* hide webkit scrollbar */}
      <style>{`div::-webkit-scrollbar { display: none; }`}</style>
    </div>
  );
}

function FeaturedCard({ book, onClick }: { book: Book; onClick: () => void }) {
  const cover =
    (book as any).coverImage ||
    (book as any).photo ||
    (book as any).coverUrl ||
    '';
  return (
    <button
      onClick={onClick}
      className="w-[120px] flex-shrink-0 text-left"
    >
      <div
        className="w-[120px] h-[170px] rounded-2xl overflow-hidden mb-2"
        style={{
          background: '#E2E8F0',
          boxShadow: '0 10px 24px -8px rgba(15, 23, 42, 0.18)',
        }}
      >
        {cover ? (
          <img
            src={cover}
            alt={book.title}
            className="w-full h-full object-cover"
          />
        ) : null}
      </div>
      <p
        className="text-[13px] font-semibold leading-tight truncate"
        style={{ color: TEXT_PRIMARY }}
      >
        {book.title}
      </p>
      <p
        className="text-[12px] mt-0.5 truncate"
        style={{ color: TEXT_MUTED }}
      >
        {(book as any).authorName || 'Unknown'}
      </p>
    </button>
  );
}

function PopularCard({ book, onClick }: { book: Book; onClick: () => void }) {
  const cover =
    (book as any).coverImage ||
    (book as any).photo ||
    (book as any).coverUrl ||
    '';
  // Backend doesn't yet expose a per-book rating; show a placeholder so
  // the design matches without inventing data.
  const rating = (book as any).averageRating;
  return (
    <button
      onClick={onClick}
      className="w-[100px] flex-shrink-0 text-left"
    >
      <div
        className="w-[100px] h-[140px] rounded-2xl overflow-hidden mb-2"
        style={{
          background: '#E2E8F0',
          boxShadow: '0 8px 18px -6px rgba(15, 23, 42, 0.15)',
        }}
      >
        {cover ? (
          <img
            src={cover}
            alt={book.title}
            className="w-full h-full object-cover"
          />
        ) : null}
      </div>
      <p
        className="text-[12px] font-semibold leading-tight truncate"
        style={{ color: TEXT_PRIMARY }}
      >
        {book.title}
      </p>
      <p
        className="text-[11px] mt-0.5 truncate"
        style={{ color: TEXT_MUTED }}
      >
        {(book as any).authorName || 'Unknown'}
      </p>
      {typeof rating === 'number' && rating > 0 && (
        <div className="flex items-center gap-1 mt-1">
          <span className="text-[11px]" style={{ color: '#FFB800' }}>★</span>
          <span className="text-[11px] font-medium" style={{ color: TEXT_PRIMARY }}>
            {rating.toFixed(1)}
          </span>
        </div>
      )}
    </button>
  );
}

/* ====================================================================
 *  Navigation helper
 * ==================================================================== */

// The Book screen reads `bookStore.selectedBook` rather than router state
// — passing the observable through navigate() throws "could not be cloned"
// because mobx proxies aren't structuredClone-able. toJS() converts the
// proxy to a plain object before stashing it; navigate() is parameterless.
function openBook(navigate: ReturnType<typeof useNavigate>, book: Book) {
  bookStore.setSelectedBook(toJS(book));
  navigate('/book');
}

export default HomeScreen;
